import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  CalendarClock, RefreshCw, AlertTriangle, CheckCircle2, Shield, XCircle,
  Filter, ExternalLink, Clock, Settings,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

// ── Types ────────────────────────────────────────────────────────────────────

type State = 'supported' | 'ending_soon' | 'security_only' | 'eol' | 'unknown'

interface Cycle {
  cycle: string
  release_date: string | null
  eol: string | null
  support_until: string | null
  latest: string | null
  latest_release_date: string | null
  lts: boolean
  link: string | null
  state: State
  days_to_eol: number | null
  // Flat-list enrichment
  product?: string
  product_slug?: string
  category?: string
}

interface Product {
  slug: string
  name: string
  category: string
  icon: string
  cycles: Cycle[]
  latest_supported?: Cycle | null
  error?: string
}

interface EOLData {
  products: Product[]
  ending_soon: Cycle[]
  recently_eol: Cycle[]
  stats: {
    products_tracked: number
    products_reachable: number
    cycles_total: number
    ending_soon_count: number
  }
  today: string
  source: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const CATEGORIES: { id: string; label: string; color: string }[] = [
  { id: 'runtime',   label: 'Languages / Runtimes',  color: 'text-blue' },
  { id: 'framework', label: 'Frameworks',            color: 'text-violet' },
  { id: 'database',  label: 'Databases',             color: 'text-emerald' },
  { id: 'infra',     label: 'Infrastructure',        color: 'text-amber' },
  { id: 'os',        label: 'Operating Systems',     color: 'text-cyan' },
  { id: 'browser',   label: 'Browsers',              color: 'text-orange' },
]

const STATE_META: Record<State, { label: string; color: string; bg: string; border: string; icon: React.ElementType }> = {
  supported:      { label: 'Supported',      color: 'text-emerald',     bg: 'bg-emerald/10', border: 'border-emerald/30', icon: CheckCircle2 },
  security_only:  { label: 'Security only',  color: 'text-amber-light', bg: 'bg-amber/10',   border: 'border-amber/30',   icon: Shield },
  ending_soon:    { label: 'Ending soon',    color: 'text-orange',      bg: 'bg-orange/10',  border: 'border-orange/30',  icon: AlertTriangle },
  eol:            { label: 'End of life',    color: 'text-rose',        bg: 'bg-rose/10',    border: 'border-rose/30',    icon: XCircle },
  unknown:        { label: 'Unknown',        color: 'text-text-muted',  bg: 'bg-surface-raised',border: 'border-border',   icon: Clock },
}

function formatDays(n: number | null): string {
  if (n == null) return '—'
  if (n < 0) return `${-n}d ago`
  if (n === 0) return 'today'
  if (n < 30) return `${n}d`
  if (n < 365) return `${Math.round(n / 30)}mo`
  return `${(n / 365).toFixed(1)}y`
}

function formatDate(s: string | null): string {
  if (!s) return '—'
  return s.slice(0, 10)
}

// ── Timeline bar showing cycle lifespan ──────────────────────────────────────

function TimelineBar({ cycles, today }: { cycles: Cycle[]; today: string }) {
  // Window: 2 years behind → 3 years ahead of today
  const todayD = new Date(today).getTime()
  const startD = todayD - 365 * 2 * 86400_000
  const endD = todayD + 365 * 3 * 86400_000
  const span = endD - startD
  const todayPct = ((todayD - startD) / span) * 100

  const rows = cycles
    .filter(c => c.release_date && c.eol && c.eol !== '1970-01-01')
    .slice(0, 5)

  return (
    <div className="space-y-1">
      <div className="relative h-4 flex items-center">
        {/* today marker */}
        <div
          className="absolute top-0 bottom-0 w-px bg-text-muted z-10"
          style={{ left: `${todayPct}%` }}
          title="Today"
        />
        <span
          className="absolute top-0 text-[9px] text-text-muted -translate-x-1/2"
          style={{ left: `${todayPct}%` }}
        >
          today
        </span>
      </div>
      <div className="space-y-1">
        {rows.map(c => {
          const relD = new Date(c.release_date!).getTime()
          const eolD = new Date(c.eol!).getTime()
          const leftPct = Math.max(0, ((relD - startD) / span) * 100)
          const widthPct = Math.min(100 - leftPct, ((eolD - relD) / span) * 100)
          const meta = STATE_META[c.state]
          return (
            <div key={c.cycle} className="relative h-5 flex items-center">
              <span className="absolute left-0 text-[10px] font-mono text-text-muted w-10 shrink-0">
                {c.cycle}
              </span>
              <div className="absolute left-10 right-0 h-full">
                <div
                  className={clsx('absolute top-0.5 bottom-0.5 rounded-sm', meta.bg, meta.border, 'border')}
                  style={{ left: `${leftPct}%`, width: `${Math.max(0.5, widthPct)}%` }}
                  title={`${c.cycle}: ${formatDate(c.release_date)} → ${formatDate(c.eol)}`}
                />
                <div
                  className="absolute top-0 bottom-0 w-px bg-text-muted opacity-40"
                  style={{ left: `${todayPct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Cycle row ────────────────────────────────────────────────────────────────

function CycleRow({ cycle, showProduct = false }: { cycle: Cycle; showProduct?: boolean }) {
  const meta = STATE_META[cycle.state]
  const Icon = meta.icon
  return (
    <div className={clsx('flex items-center gap-2 py-1.5 text-[11px] border-b border-border/40 last:border-0')}>
      {showProduct && (
        <div className="w-28 shrink-0">
          <div className="text-text-primary font-semibold text-[11px]">{cycle.product}</div>
          <div className="text-[9px] text-text-muted">{cycle.category}</div>
        </div>
      )}
      <div className="w-12 shrink-0 font-mono text-text-primary text-[11px]">{cycle.cycle}</div>
      <div className="w-14 shrink-0">
        {cycle.lts && (
          <span className="px-1 py-0.5 rounded text-[9px] font-semibold bg-blue/10 text-blue">LTS</span>
        )}
      </div>
      <div className="w-20 shrink-0 font-mono text-text-secondary text-[10px]">
        {cycle.latest || '—'}
      </div>
      <div className="w-24 shrink-0 font-mono text-text-muted text-[10px]">
        {formatDate(cycle.release_date)}
      </div>
      <div className="w-24 shrink-0 font-mono text-text-muted text-[10px]">
        {formatDate(cycle.eol)}
      </div>
      <div className="flex items-center gap-1 min-w-0">
        <Icon size={11} className={meta.color} />
        <span className={clsx('text-[10px] font-semibold', meta.color)}>{meta.label}</span>
        <span className="text-[10px] text-text-muted">· {formatDays(cycle.days_to_eol)}</span>
      </div>
    </div>
  )
}

// ── Product card ─────────────────────────────────────────────────────────────

function ProductCard({ product, today }: { product: Product; today: string }) {
  const supported = product.latest_supported
  const meta = supported ? STATE_META[supported.state] : null
  return (
    <div className="card p-3 hover:bg-surface-raised/30 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold text-text-primary">{product.name}</span>
            {supported?.lts && (
              <span className="px-1 py-0.5 rounded text-[9px] font-semibold bg-blue/10 text-blue">LTS</span>
            )}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-text-muted">{product.category}</div>
        </div>
        {meta && supported && (
          <div className={clsx('flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px]', meta.bg, meta.border)}>
            <meta.icon size={10} className={meta.color} />
            <span className={clsx('font-semibold', meta.color)}>{meta.label}</span>
          </div>
        )}
      </div>

      {supported ? (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] mb-2">
          <div>
            <div className="text-text-muted">Latest</div>
            <div className="font-mono text-text-primary">{supported.latest || supported.cycle}</div>
          </div>
          <div>
            <div className="text-text-muted">EOL in</div>
            <div className="font-mono text-text-primary">{formatDays(supported.days_to_eol)}</div>
          </div>
          <div>
            <div className="text-text-muted">Released</div>
            <div className="font-mono text-text-secondary">{formatDate(supported.release_date)}</div>
          </div>
          <div>
            <div className="text-text-muted">EOL date</div>
            <div className="font-mono text-text-secondary">{formatDate(supported.eol)}</div>
          </div>
        </div>
      ) : product.error ? (
        <div className="text-[10px] text-rose">{product.error}</div>
      ) : (
        <div className="text-[10px] text-text-muted">No supported cycle data</div>
      )}

      {product.cycles.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <TimelineBar cycles={product.cycles} today={today} />
        </div>
      )}
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function EOLCalendarPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<EOLData | null>(null)
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/eol')
      setData(data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const filteredProducts = useMemo(() => {
    if (!data) return []
    let prods = data.products
    if (category) prods = prods.filter(p => p.category === category)
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      prods = prods.filter(p => p.name.toLowerCase().includes(q))
    }
    return prods
  }, [data, category, searchQuery])

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }

  if (!data) {
    return <div className="text-text-muted text-sm">No data</div>
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <CalendarClock size={20} className="text-cyan" /> EOL / Evergreen Tracker
          </h1>
          <p className="text-[10px] text-text-secondary">
            {data.stats.products_tracked} runtimes, frameworks, databases, OSes —
            live from endoflife.date · {data.stats.ending_soon_count} ending in the next 90 days
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/app/eol-manage')}
            className="btn-secondary text-[11px] flex items-center gap-1.5"
            title="Add, edit or disable tracked products"
          >
            <Settings size={12} /> Manage products
          </button>
          <button
            type="button"
            onClick={fetchData}
            className="btn-ghost text-[11px] flex items-center gap-1.5"
            title="Refresh"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Ending soon</div>
          <div className="text-lg font-bold text-orange mt-1">{data.stats.ending_soon_count}</div>
          <div className="text-[10px] text-text-muted">cycles with ≤90 days to EOL</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Products tracked</div>
          <div className="text-lg font-bold text-blue mt-1">{data.stats.products_reachable}/{data.stats.products_tracked}</div>
          <div className="text-[10px] text-text-muted">reachable from endoflife.date</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Recent EOLs</div>
          <div className="text-lg font-bold text-rose mt-1">{data.recently_eol.length}</div>
          <div className="text-[10px] text-text-muted">reached EOL in last 180 days</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Total cycles</div>
          <div className="text-lg font-bold text-text-primary mt-1">{data.stats.cycles_total}</div>
          <div className="text-[10px] text-text-muted">across tracked products</div>
        </div>
      </div>

      {/* Ending soon table */}
      {data.ending_soon.length > 0 && (
        <div className="card p-4 border border-orange/20">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-orange" />
            Ending soon · next 90 days
            <span className="text-[10px] text-text-muted font-normal">prioritise upgrade planning</span>
          </h2>
          <div className="overflow-x-auto">
            <div className="flex items-center gap-2 py-1 text-[9px] uppercase tracking-wider text-text-muted border-b border-border">
              <div className="w-28 shrink-0">Product</div>
              <div className="w-12 shrink-0">Cycle</div>
              <div className="w-14 shrink-0">Type</div>
              <div className="w-20 shrink-0">Latest</div>
              <div className="w-24 shrink-0">Released</div>
              <div className="w-24 shrink-0">EOL</div>
              <div>Status</div>
            </div>
            {data.ending_soon.map((c, i) => (
              <CycleRow key={`${c.product_slug}-${c.cycle}-${i}`} cycle={c} showProduct />
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Filter size={12} className="text-text-muted" />
          <button
            type="button"
            onClick={() => setCategory('')}
            className={clsx(
              'px-2 py-1 text-[10px] rounded border transition-colors',
              !category
                ? 'bg-blue/10 border-blue/30 text-blue-light'
                : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
            )}
          >
            All
          </button>
          {CATEGORIES.map(c => (
            <button
              type="button"
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={clsx(
                'px-2 py-1 text-[10px] rounded border transition-colors',
                category === c.id
                  ? 'bg-blue/10 border-blue/30 text-blue-light'
                  : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search product…"
          className="input py-1 text-[11px] ml-auto w-48"
          title="Search product"
        />
      </div>

      {/* Product grid by category */}
      {CATEGORIES.filter(c => !category || c.id === category).map(cat => {
        const prods = filteredProducts.filter(p => p.category === cat.id)
        if (prods.length === 0) return null
        return (
          <div key={cat.id} className="space-y-2">
            <h2 className={clsx('text-sm font-semibold flex items-center gap-2', cat.color)}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {cat.label}
              <span className="text-[10px] text-text-muted font-normal">{prods.length}</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {prods.map(p => (
                <ProductCard key={p.slug} product={p} today={data.today} />
              ))}
            </div>
          </div>
        )
      })}

      {/* Footer / legend */}
      <div className="card p-3">
        <div className="flex items-center gap-3 flex-wrap text-[10px]">
          <span className="text-text-muted uppercase tracking-wider font-semibold">Legend:</span>
          {(Object.entries(STATE_META) as [State, typeof STATE_META[State]][]).map(([k, m]) => {
            const Icon = m.icon
            return (
              <span key={k} className="flex items-center gap-1">
                <Icon size={10} className={m.color} />
                <span className={m.color}>{m.label}</span>
              </span>
            )
          })}
          <span className="text-text-muted ml-auto">
            Data: <a href="https://endoflife.date" target="_blank" rel="noreferrer noopener" className="hover:underline inline-flex items-center gap-0.5">endoflife.date <ExternalLink size={9} /></a>
            · cached 6h
          </span>
        </div>
      </div>
    </div>
  )
}
